import { DiagnosticRating } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models/diagnostic';

import { ScoreCriteria } from ':shared/enums';

interface ScoreCriteriaDetails {
    [ScoreCriteria.INCONSISTENCIES]: {
        rating: DiagnosticRating;
        count: number | undefined;
    };
    [ScoreCriteria.BETTER_LOCAL_COMPETITORS]: {
        rating: DiagnosticRating;
        count: number;
    };
    [ScoreCriteria.REVIEW_COUNT]: {
        rating: DiagnosticRating;
        count: number;
        diff: number;
    };
    [ScoreCriteria.INSTAGRAM_VISIBILITY]: {
        hasInstagram: boolean;
        rating: DiagnosticRating;
    };
}
export class ScoreCriterions {
    diagnosticId: string;
    private _scoreDetailsPerCriteria: ScoreCriteriaDetails;

    isClient: boolean;

    constructor(diagnostic: Diagnostic) {
        const inconsistenciesRating = diagnostic.getInconsistencyRating();
        const inconsistenciesCount = diagnostic.inconsistencies?.inconsistencyCount;

        const googleRating = diagnostic.getGoogleRating();
        const betterLocalCompetitors = diagnostic.getBetterLocalCompetitorCount();

        const reviewRating = diagnostic.getReviewCountRating();
        const reviewCount = diagnostic.reviewCount;
        const reviewDiff = diagnostic.getReviewCountDiff();

        const instagramVisibilityRating = diagnostic.getInstagramVisibilityRating();

        this.isClient = diagnostic.isMalouRestaurant;
        this.diagnosticId = diagnostic.id;

        this._scoreDetailsPerCriteria = {
            [ScoreCriteria.INCONSISTENCIES]: {
                rating: inconsistenciesRating,
                count: inconsistenciesCount,
            },
            [ScoreCriteria.BETTER_LOCAL_COMPETITORS]: {
                rating: googleRating,
                count: betterLocalCompetitors,
            },
            [ScoreCriteria.REVIEW_COUNT]: {
                rating: reviewRating,
                count: reviewCount,
                diff: reviewDiff,
            },
            [ScoreCriteria.INSTAGRAM_VISIBILITY]: {
                hasInstagram: !!diagnostic.instagramPage?.name,
                rating: instagramVisibilityRating,
            },
        };
    }

    getScoreCriteriaDetails(
        criteria: ScoreCriteria
    ):
        | ScoreCriteriaDetails['inconsistencies']
        | ScoreCriteriaDetails['better_local_competitors']
        | ScoreCriteriaDetails['review_count']
        | ScoreCriteriaDetails['instagram_visibility'] {
        return this._scoreDetailsPerCriteria?.[criteria];
    }

    getSortedScoreCriterions(): ScoreCriteria[] {
        const valuesToSort = Object.values(ScoreCriteria).filter(
            (value) => value !== ScoreCriteria.INSTAGRAM_VISIBILITY || this._scoreDetailsPerCriteria[value].hasInstagram
        );
        const goodRatingCriterions = valuesToSort.filter(
            (scoreCriteria) => this._scoreDetailsPerCriteria[scoreCriteria].rating === DiagnosticRating.GOOD
        );
        const averageRatingCriterions = valuesToSort.filter(
            (scoreCriteria) => this._scoreDetailsPerCriteria[scoreCriteria].rating === DiagnosticRating.AVERAGE
        );
        const badRatingCriterions = valuesToSort.filter(
            (scoreCriteria) => this._scoreDetailsPerCriteria[scoreCriteria].rating === DiagnosticRating.BAD
        );

        if (this.isClient) {
            return [...goodRatingCriterions, ...averageRatingCriterions, ...badRatingCriterions] as ScoreCriteria[];
        }
        return [...badRatingCriterions, ...averageRatingCriterions, ...goodRatingCriterions] as ScoreCriteria[];
    }
}
